/**
 * constants/backgrounds.ts — All available background images for the app.
 *
 * HOW BACKGROUNDS WORK:
 *   1. The user opens the Background Picker (BackgroundModal) and selects one.
 *   2. App.tsx stores the selection in state and in localStorage so it persists.
 *   3. The selected background's `url` is applied as a CSS `background-image`
 *      on the main app container, with a semi-transparent overlay so the
 *      background doesn't interfere with text readability.
 *   4. Selecting 'none' removes the background entirely.
 *
 * HOW TO ADD A NEW BACKGROUND:
 *   1. Place the image file (JPG recommended for photos) inside public/backgrounds/.
 *   2. Add a new entry to the array below with a unique `id`, a display `name`,
 *      and set both `url` and `thumbnail` to "/backgrounds/your-file.jpg".
 *      (Using the same file for thumbnail keeps things simple; a smaller
 *      thumbnail file can be supplied later for performance if needed.)
 *
 * All image paths are relative to the `public/` folder (served at the root URL).
 */

/** Shape of a single background option shown in the Background Picker. */
export interface Background {
  id: string;        // Unique string key — saved to localStorage to remember the user's choice
  name: string;      // Human-readable label shown in the picker UI
  url: string;       // Full-resolution image path applied as the page background
  thumbnail: string; // Preview image path shown inside the picker modal
}

export const backgrounds: Background[] = [
  // The "None" option removes any background image. url/thumbnail are empty strings.
  {
    id: 'none',
    name: 'None',
    url: '',
    thumbnail: '',
  },
  {
    id: 'mountain',
    name: 'Mountain',
    url: '/backgrounds/mountain.jpg',
    thumbnail: '/backgrounds/mountain.jpg',
  },
  {
    id: 'ocean',
    name: 'Ocean',
    url: '/backgrounds/ocean.jpg',
    thumbnail: '/backgrounds/ocean.jpg',
  },
  {
    id: 'forest',
    name: 'Forest',
    url: '/backgrounds/forest.jpg',
    thumbnail: '/backgrounds/forest.jpg',
  },
  {
    id: 'abstract',
    name: 'Abstract',
    url: '/backgrounds/abstract.jpg',
    thumbnail: '/backgrounds/abstract.jpg',
  },
  {
    id: 'city',
    name: 'City',
    url: '/backgrounds/city.jpg',
    thumbnail: '/backgrounds/city.jpg',
  },
  {
    id: 'space',
    name: 'Space',
    url: '/backgrounds/space.jpg',
    thumbnail: '/backgrounds/space.jpg',
  },
  {
    id: 'desert',
    name: 'Desert',
    url: '/backgrounds/desert.jpg',
    thumbnail: '/backgrounds/desert.jpg',
  },
  {
    id: 'sunset',
    name: 'Sunset',
    url: '/backgrounds/sunset.jpg',
    thumbnail: '/backgrounds/sunset.jpg',
  },
  {
    id: 'snow',
    name: 'Snow',
    url: '/backgrounds/snow.jpg',
    thumbnail: '/backgrounds/snow.jpg',
  },
  {
    id: 'minimal',
    name: 'Minimal',
    url: '/backgrounds/minimal.jpg',
    thumbnail: '/backgrounds/minimal.jpg',
  },
];
